<template>
  <div class="board">
    <h2 class="title">
      {{ title }}
    </h2>
    <p>{{ subtitle }}</p>
    <span>
      {{ $t('reservation') }}
      <span :style="{ cursor: hasIntegration ? 'initial' : 'pointer' }">
        {{ mainGuest.reservationCode }}
        <span class="slb-tooltip">
          {{ $t('reservationTooltip') }}
          <br />
          <button @click="editReservation">
            {{ $t('reservationTooltipClick') }}
          </button>
        </span>
      </span>
    </span>
    <ul class="board__list">
      <li
        v-for="(guest, index) in guests"
        :key="index"
      >
        <h4>
          <strong>{{
            !!guest.surname
              ? `${guest.name} ${guest.surname}`
              : guest.name || 'Nome do hóspede'
          }}</strong>
          | {{ index === 0 ? $t('mainGuest') : $t('asideGuest') }}
        </h4>
        <div
          class="status"
          :class="{ done: guest.isValid }"
        >
          <div v-if="guest.isValid">
            <IconSuccess size="xs" />
            <strong>{{ $t('done') }}</strong>
          </div>
          <div v-else>
            <IconWarning size="xs" />
            <strong>{{ $t('incomplete') }}</strong>
          </div>
        </div>
        <button
          class="cta vazado"
          @click="edit(index)"
        >
          {{ $t('edit') }}
        </button>
      </li>
    </ul>
    <div class="checkbox">
      <input
        id="confirmationSendCopyToGuest"
        v-model="confirmationSendCopyToGuest"
        type="checkbox"
        @change="checkSendCopyToGuest"
      />
      <label for="confirmationSendCopyToGuest">{{
        $t('confirmationSendCopyToGuest')
      }}</label>
    </div>
    <div class="board__actions">
      <button
        v-if="!hasIntegration"
        class="cta vazado"
        @click="add"
      >
        {{ $t('add') }}
      </button>
      <button
        class="cta"
        :class="{
          sending: isSending,
          error: error,
        }"
        :disabled="isSending || error || !isAllValidGuests"
        @click="end"
      >
        {{ sendLabel }}
      </button>
    </div>
  </div>
</template>

<script>
import IconSuccess from '@/components/icons/IconSuccess.vue';
import IconWarning from '@/components/icons/IconWarning.vue';

export default {
  name: 'Board',
  components: {
    IconSuccess,
    IconWarning,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    guests: {
      type: Array,
      default: () => [],
    },
    mainGuest: {
      type: Object,
      default: () => ({}),
    },
    isSending: {
      type: Boolean,
      default: false,
    },
    hasIntegration: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      confirmationSendCopyToGuest: false,
    };
  },
  computed: {
    isAllValidGuests() {
      return this.guests.every(({ isValid }) => isValid);
    },
    sendLabel() {
      if (this.error) {
        return this.$t('error');
      }
      if (this.isSending) {
        return this.$t('sending');
      }
      return this.$t('end');
    },
  },
  methods: {
    edit(index) {
      this.$emit('edit', index);
    },
    editReservation() {
      this.$emit('editReservation');
    },
    checkSendCopyToGuest() {
      this.$emit('checkSendCopyToGuest', this.confirmationSendCopyToGuest);
    },
    add() {
      this.$emit('add');
    },
    end() {
      this.$emit('end');
    },
  },
  i18n: {
    messages: {
      pt: {
        reservation: 'Reserva',
        reservationTooltip: 'Não é sua reserva?',
        reservationTooltipClick: 'Clique aqui para editar',
        mainGuest: 'Principal',
        asideGuest: 'Acompanhante',
        edit: 'Editar dados',
        add: 'Adicionar acompanhantes',
        end: 'Finalizar Pré check-in',
        sending: 'Enviando...',
        done: 'Concluído',
        incomplete: 'Incompleto',
        error: 'Algo errado aconteceu. Tente novamente.',
        confirmationSendCopyToGuest: 'Receber cópia do Pré check-in por e-mail',
      },
      en: {
        reservation: 'Reservation',
        reservationTooltip: 'Not your reservation?',
        reservationTooltipClick: 'Click here to edit',
        mainGuest: 'Main Guest',
        asideGuest: 'Additional guest',
        edit: 'Edit data',
        add: 'Add guests',
        end: 'Finish Pré check-in',
        sending: 'Sending...',
        done: 'Done',
        incomplete: 'Incomplete',
        error: 'Something wrong happened. Try again.',
        confirmationSendCopyToGuest:
          'Receive a copy of the Pré check-in by e-mail',
      },
      es: {
        reservation: 'Reserva',
        reservationTooltip: '¿No es tu reserva?',
        reservationTooltipClick: 'Haz clic aquí para editar',
        mainGuest: 'Huésped Principal',
        asideGuest: 'Acompañante',
        edit: 'Editar datos',
        add: 'Agregar acompañante',
        end: 'Terminar Pré check-in',
        sending: 'Enviando...',
        done: 'Concluido',
        incomplete: 'Incompleto',
        error: 'Ocurrió un error. Inténtalo nuevamente.',
        confirmation: 'Ya he registrado a todos los acompañantes',
        confirmationSendCopyToGuest:
          'Recibir una copia del Pré check-in por e-mail',
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.board {
  max-width: 1046px;

  > h2 {
    margin-bottom: 16px;
  }

  > p {
    color: $lb-medium-gray;
    font-size: $size-normal;
    margin-bottom: 60px;
  }

  > span {
    font-weight: bold;
    font-size: 20px;
    color: $lb-black;
    margin-bottom: 15px;

    > span {
      color: var(--primaryColor);
      position: relative;
      cursor: pointer;
      text-decoration: underline;

      .slb-tooltip {
        opacity: 0;
        visibility: hidden;
        cursor: initial;

        > button {
          color: var(--primaryColor);
          cursor: pointer;
          text-decoration: underline;
          border: 0;
          padding: 0;
          background: none;
        }
      }
      &:hover,
      &:focus-within {
        .slb-tooltip {
          opacity: 1;
          visibility: initial;
        }
      }
    }
  }
  &__list {
    li {
      margin-top: 15px;
      padding: 20px;
      box-shadow: 4px 4px 20px rgba($lb-black, 0.25);
      border: 1px solid $lb-light-gray;
      border-radius: 5px;

      @include media-breakpoint-up(sm) {
        display: flex;
        align-items: center;
        padding: 31px 37px;
      }

      h4 {
        margin: 0 20px 0 0;
        max-width: 520px;
        font-size: 20px;
        font-weight: $lb-light;
        color: $lb-black;

        strong {
          font-weight: bold !important;
        }
      }

      .cta {
        margin-top: 15px;

        @include media-breakpoint-up(sm) {
          margin-top: 0;
          width: 209px;
          text-transform: none;
          margin-left: 20px;
        }
      }
    }

    .status {
      flex-shrink: 0;
      width: 140px;
      font-size: 18px;
      color: $warning;
      margin: 20px 0;
      text-align: left;
      margin-top: 20px;
      margin-bottom: 20px;

      .icon {
        padding: 2px;
        border-radius: 100%;
        margin-right: 10px;
        color: white;
        background-color: $warning;
      }

      &.done {
        color: $success;

        .icon {
          background-color: $success;
        }
      }

      @include media-breakpoint-up(sm) {
        margin: 0 0 0 auto;
      }
    }
  }
  .checkbox {
    margin-top: 35px;
  }

  &__actions {
    @include media-breakpoint-up(sm) {
      display: flex;
      justify-content: flex-end;
      gap: 15px;
    }

    .cta {
      margin-top: 35px;
      transition: 0.15s all;

      &.error {
        background-color: $lb-red;
      }
    }
  }
}
</style>
